import { yupResolver } from '@hookform/resolvers/yup'
import { colors } from '@kmns-internal/furikake'
import { LocationOn } from '@mui/icons-material'
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import type {
  AddReportsExportsRequestBody,
  ReportExportFilterCondition,
} from '@ulysses-inc/harami_api_client'
import { AppRoleEnum } from '@ulysses-inc/harami_api_client'
// eslint-disable-next-line import/no-duplicates
import { addDays, format, parseISO } from 'date-fns'
// eslint-disable-next-line import/no-duplicates
import { ja } from 'date-fns/locale'
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import {
  Controller,
  DefaultValues,
  SubmitHandler,
  useForm,
  useWatch,
} from 'react-hook-form'
import { useHistory, useLocation } from 'react-router-dom'
import PlaceGroupSVG from 'src/assets/icons/place_groups.svg?react'
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage'
import { useSnackbar } from 'src/components/snackbar/useSnackbar'
import { useLimitedFeatureAvailability } from 'src/features/featureAvailability/useLimitedFeatureAvailability'
import { useIsLobsterEnabled } from 'src/features/labelCheck/hooks/useIsLobsterEnabled'
import { ReportExportDetailHeader } from 'src/features/reportExport/detail/ReportExportDetailHeader'
import { ReportExportDetailSendConfirmationV2 } from 'src/features/reportExport/detail/ReportExportDetailSendConfirmationV2'
import { useMutationAddBulkReportExport } from 'src/features/reportExport/detail/api/useMutationAddBulkReportExport'
import { useMutationAddReportExport } from 'src/features/reportExport/detail/api/useMutationAddReportExport'
import { useQueryTemplates } from 'src/features/reportExport/detail/api/useQueryTemplates'
import { TitleAndContent } from 'src/features/reportExport/detail/components/TitleAndContent'
import { buildReexportDefaults } from 'src/features/reportExport/detail/utils/buildReexportDefaults'
import {
  buildPasteResultMessage,
  containsTemplateSeparator,
  matchPastedTemplates,
  parsePastedTemplateNames,
  unionTemplateIds,
} from 'src/features/reportExport/detail/utils/matchPastedTemplates'
import { pruneReexportConditions } from 'src/features/reportExport/detail/utils/pruneReexportConditions'
import { useAutoOutputFileName } from 'src/features/reportExport/detail/utils/useAutoOutputFileName'
import { useQueryPlaceNodes } from 'src/features/tanStackQuery/commonQueries/useQueryPlaceNodes'
import { useQueryUserPlaceNodes } from 'src/features/tanStackQuery/commonQueries/useQueryUserPlaceNodes'
import yup from 'src/features/validation/yup'
import { useTogglable } from 'src/hooks/useTogglable'
import { appRole } from 'src/libraries/roles/appRole'
import { countMultibyteChars } from 'src/util/countMultibyteChars'
import { toDateString } from 'src/util/datetime/toDateString'

const BULK_OUTPUT_FILENAME = 'レポート一括出力'

const formValuesSchema = yup
  .object({
    endDate: yup
      .string()
      .nullable()
      .required('終了日を入力してください')
      .test(
        'is-greater-than-or-equal-to',
        '終了日は開始日よりも後の日付に設定してください',
        function (endDate) {
          const { startDate } = this.parent
          if (!endDate || !startDate) return true
          return parseISO(endDate) >= parseISO(startDate)
        },
      )
      .test(
        'max-92-days',
        '指定できる最長期間は92日間です',
        function (endDate) {
          const { startDate } = this.parent
          if (!endDate || !startDate) return true
          return (
            parseISO(endDate).valueOf() - parseISO(startDate).valueOf() <=
            91 * 24 * 60 * 60 * 1000
          )
        },
      ),
    outputFileName: yup
      .string()
      .required('出力ファイル名を入力してください')
      .test(
        'max-60-multibyte-chars',
        '出力ファイル名は60文字以下である必要があります',
        function (outputFileName) {
          if (!outputFileName) return true
          return countMultibyteChars(outputFileName) <= 60
        },
      ),
    placeNodeUuids: yup
      .array(yup.string().required())
      .when('templateIds', {
        is: (templateIds: number[] | undefined) =>
          (templateIds?.length ?? 0) <= 1,
        then: schema => schema.min(1, '現場を選択してください').required(),
        otherwise: schema => schema.optional(),
      })
      .required(),
    startDate: yup.string().nullable().required('開始日を入力してください'),
    templateIds: yup
      .array(yup.number().required())
      .min(1, 'ひな形を選択してください')
      // API 側 (POST /reports/exports/bulk) の maxItems: 100 に合わせる
      .max(100, '選択できるひな形は最大100件までです')
      .required(),
  })
  .required()

type ValidatedFormValues = yup.InferType<typeof formValuesSchema>

const defaultFormValues: DefaultValues<ValidatedFormValues> = {
  endDate: undefined,
  outputFileName: '',
  placeNodeUuids: [],
  startDate: undefined,
  templateIds: [],
}

// 一覧の「再出力」から渡される、過去の出力条件
type ReexportLocationState = {
  reexportFilterCondition?: ReportExportFilterCondition
  // 過去の出力ファイル名。ユーザーが条件を変えるまで引き継ぐ
  reexportFileName?: string
}

/**
 * レポート出力依頼画面 (即時出力・複数ひな形対応)。
 * ひな形 1 件選択時は `POST /reports/exports`、 2 件以上で `POST /reports/exports/bulk` を呼ぶ。
 */

// 関数内で定義すると毎レンダー新参照になり、 React reconciler が Chip / Option を
// unmount → remount するためトップレベルに置く
const PlaceNodeIcon = ({ type }: { type: 'place' | 'placeGroup' }) =>
  type === 'placeGroup' ? (
    <PlaceGroupSVG style={{ width: '16px' }} color={colors.action.active} />
  ) : (
    <LocationOn sx={{ width: '16px', color: colors.action.active }} />
  )

export const ReportExportDetailV2 = () => {
  const history = useHistory()
  const location = useLocation<ReexportLocationState>()
  const { showSuccessSnackbar, showInfoSnackbar } = useSnackbar()
  const reexportFilterCondition = location.state?.reexportFilterCondition
  const reexportFileName = location.state?.reexportFileName
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false)
  const [errorDialogOpen, setErrorDialogOpen] = useState(false)

  const { isLobsterEnabled } = useIsLobsterEnabled()

  const templatesQuery = useQueryTemplates()
  const userBelongingPlacesQuery = useQueryUserPlaceNodes()
  const allPlacesQuery = useQueryPlaceNodes()

  const { getFeatureAvailability } = useLimitedFeatureAvailability()
  const canBulkExport = getFeatureAvailability('bulkReportExport') === 'yes'

  const isOwner = appRole() === AppRoleEnum.OWNER
  // 単一ひな形時のみ有効。 一括出力は常に会社の全現場対象でトグルの影響を受けない。
  const [includeNonBelongingPlaces, setIncludeNonBelongingPlaces] =
    useState(false)

  const effectivePlaces =
    isOwner && includeNonBelongingPlaces
      ? (allPlacesQuery.data ?? [])
      : (userBelongingPlacesQuery.data ?? [])

  const isPending =
    templatesQuery.isPending ||
    userBelongingPlacesQuery.isPending ||
    allPlacesQuery.isPending
  const isError =
    templatesQuery.isError ||
    userBelongingPlacesQuery.isError ||
    allPlacesQuery.isError
  const userHasAssignedPlaces =
    userBelongingPlacesQuery.data && userBelongingPlacesQuery.data?.length > 0

  const { mutate: addReportExportMutation, isPending: isAddingReportExport } =
    useMutationAddReportExport()
  const {
    mutate: addBulkReportExportMutation,
    isPending: isAddingBulkReportExport,
  } = useMutationAddBulkReportExport()

  const sendConfirmationModal = useTogglable()

  const {
    control,
    formState: { errors, isDirty, dirtyFields },
    getValues,
    handleSubmit,
    setValue,
    trigger,
    watch,
  } = useForm<ValidatedFormValues>({
    defaultValues: {
      ...defaultFormValues,
      ...buildReexportDefaults(reexportFilterCondition),
      // 過去の出力ファイル名を初期値に引き継ぐ。条件変更までは自動生成で上書きしない
      ...(reexportFileName ? { outputFileName: reexportFileName } : {}),
    },
    mode: 'onChange',
    resolver: yupResolver(formValuesSchema),
  })

  const formValues = watch()
  // watch().templateIds は毎レンダー新参照になるので、 useEffect deps 用に
  // 安定参照を返す useWatch を使う
  const templateIds = useWatch({ control, name: 'templateIds' })

  const isBulk = templateIds.length > 1

  // 確認ダイアログに渡す現場名・ひな形名は名前解決できたものだけを対象にする。
  // 件数ラベル(N件)と実表示が食い違わないよう、join と件数を同じ配列から導出する。
  const resolvedPlaceNames = isBulk
    ? []
    : formValues.placeNodeUuids
        .map(
          placeNodeUuid =>
            effectivePlaces.find(place => place.uuid === placeNodeUuid)?.name,
        )
        .filter((n): n is string => !!n)

  const resolvedTemplateNames =
    !templatesQuery.data || templateIds.length === 0
      ? []
      : templateIds
          .map(
            id =>
              templatesQuery.data?.find(template => template.id === id)?.name,
          )
          .filter((n): n is string => !!n)

  const stringifiedFormValues = {
    endDate: ((): string => {
      if (!formValues.endDate) return ''
      return format(parseISO(formValues.endDate), 'yyyyMMdd')
    })(),
    endDateWithSlash: ((): string => {
      if (!formValues.endDate) return ''
      return format(parseISO(formValues.endDate), 'yyyy/MM/dd')
    })(),
    // 確認ダイアログは件数を丸めず全件表示する。複数項目のときダイアログ側で
    // 件数ラベルを付け、固定高さのスクロール領域に収める。
    placeNames: isBulk ? 'すべての現場' : resolvedPlaceNames.join('、'),
    templateNames: resolvedTemplateNames.join('、'),
    startDate: ((): string => {
      if (!formValues.startDate) return ''
      return format(parseISO(formValues.startDate), 'yyyyMMdd')
    })(),
    startDateWithSlash: ((): string => {
      if (!formValues.startDate) return ''
      return format(parseISO(formValues.startDate), 'yyyy/MM/dd')
    })(),
  }

  // 出力ファイル名 (`<前半>_<開始日>-<終了日>`) を組み立てる。日付は選択期間に
  // 追従し、前半はユーザーがカスタムした名前なら維持する。再出力で引き継いだ
  // カスタム名 (例 UH_...) も前半として保持され、日付だけ追従する。
  const resolveTemplateName = useCallback(
    (id: number) => templatesQuery.data?.find(t => t.id === id)?.name,
    [templatesQuery.data],
  )
  const getOutputFileName = useCallback(
    () => getValues('outputFileName'),
    [getValues],
  )
  const setOutputFileName = useCallback(
    (name: string) => setValue('outputFileName', name),
    [setValue],
  )
  const triggerOutputFileNameValidation = useCallback(
    () => void trigger('outputFileName'),
    [trigger],
  )
  useAutoOutputFileName({
    templateIds,
    startYMD: stringifiedFormValues.startDate,
    endYMD: stringifiedFormValues.endDate,
    templatesLoaded: Boolean(templatesQuery.data),
    resolveTemplateName,
    bulkLabel: BULK_OUTPUT_FILENAME,
    getOutputFileName,
    setOutputFileName,
    triggerValidation: triggerOutputFileNameValidation,
  })

  // 再出力プリフィル後、現存しないひな形/現場を form から除外する。
  // 死んだ id がそのまま送信されるのを防ぎ、除外をユーザーに通知する。
  const hasReconciledReexport = useRef(false)
  useEffect(() => {
    if (hasReconciledReexport.current) return
    if (!reexportFilterCondition) return
    if (!templatesQuery.data) return
    // 現場の存在判定には現場一覧の確定が必要
    if (userBelongingPlacesQuery.isPending || allPlacesQuery.isPending) return

    hasReconciledReexport.current = true

    // 全現場の和集合(オーナーの全現場 ∪ 所属現場)で存在判定し、表示対象外なだけの
    // 現場を誤って除外しない。現場一覧が取得できない場合は現場整流をスキップする。
    const placeUniverse = [
      ...(allPlacesQuery.data ?? []),
      ...(userBelongingPlacesQuery.data ?? []),
    ]
    const availablePlaceUuids =
      placeUniverse.length > 0 ? new Set(placeUniverse.map(p => p.uuid)) : null

    const result = pruneReexportConditions({
      templateIds: getValues('templateIds') ?? [],
      placeNodeUuids: getValues('placeNodeUuids') ?? [],
      availableTemplateIds: new Set(templatesQuery.data.map(t => t.id)),
      availablePlaceUuids,
    })

    if (result.droppedTemplateCount > 0)
      setValue('templateIds', result.prunedTemplateIds)
    if (result.droppedPlaceCount > 0)
      setValue('placeNodeUuids', result.prunedPlaceUuids)
    if (result.notice) showInfoSnackbar(result.notice)
  }, [
    allPlacesQuery.data,
    allPlacesQuery.isPending,
    getValues,
    reexportFilterCondition,
    setValue,
    showInfoSnackbar,
    templatesQuery.data,
    userBelongingPlacesQuery.data,
    userBelongingPlacesQuery.isPending,
  ])

  const backToPreviousScreen = () => {
    const execute = () => history.push('/reportExports')
    if (!isDirty) {
      execute()
      return
    }
    setDiscardDialogOpen(true)
  }

  const onSubmit: SubmitHandler<ValidatedFormValues> = validatedFormValues => {
    const [templateId] = validatedFormValues.templateIds

    // 一括出力 API は管理者限定のため、利用できない場合は単一出力に倒す
    if (canBulkExport && validatedFormValues.templateIds.length > 1) {
      // 単一→複数切替時に残存した選択値が混入しないよう、 ここで明示的に全現場を採用する
      const placeNodeUuidList = (allPlacesQuery.data ?? []).map(p => p.uuid)

      addBulkReportExportMutation(
        {
          fileName: validatedFormValues.outputFileName,
          filterCondition: {
            templateIds: validatedFormValues.templateIds,
            placeNodeUuidList,
            startDateTime: parseISO(validatedFormValues.startDate),
            endDateTime: addDays(parseISO(validatedFormValues.endDate), 1),
          },
        },
        {
          onSuccess: () => {
            sendConfirmationModal.close()
            history.push('/reportExports', { showSuccessMessage: true })
          },
          onError: () => {
            sendConfirmationModal.close()
            setErrorDialogOpen(true)
          },
        },
      )
      return
    }

    if (templateId == null) {
      // 型安全のための保険 (バリデーションを通っていればここには到達しない)
      return
    }

    const requestBody: AddReportsExportsRequestBody = {
      fileName: validatedFormValues.outputFileName,
      filterCondition: {
        endDateTime: addDays(parseISO(validatedFormValues.endDate), 1),
        placeNodeUuidList: validatedFormValues.placeNodeUuids,
        startDateTime: parseISO(validatedFormValues.startDate),
        templateId,
      },
    }

    addReportExportMutation(requestBody, {
      onSuccess: () => {
        sendConfirmationModal.close()
        history.push('/reportExports', { showSuccessMessage: true })
      },
      onError: () => {
        sendConfirmationModal.close()
        setErrorDialogOpen(true)
      },
    })
  }

  const renderWithCommonContainer = (children: ReactNode) => (
    <Stack>
      <ReportExportDetailHeader
        onBackButtonClicked={backToPreviousScreen}
        onOutputButtonClicked={handleSubmit(sendConfirmationModal.open)}
        isOutputButtonDisabled={
          isPending || isAddingReportExport || isAddingBulkReportExport
        }
      />
      <Box sx={{ p: 4, mx: 'auto' }} maxWidth="sm">
        {children}
      </Box>
    </Stack>
  )

  if (isPending) {
    return renderWithCommonContainer(
      <Stack justifyContent="center" alignItems="center">
        <CircularProgress size={60} aria-label="データを取得中" />
      </Stack>,
    )
  }

  if (isError) {
    return renderWithCommonContainer(
      <ErrorMessage noMargin>エラーが発生しました</ErrorMessage>,
    )
  }

  return (
    <>
      {renderWithCommonContainer(
        <Stack component="form" gap={2} aria-label="新規でレポート出力">
          {/* ひな形 */}
          <TitleAndContent title="ひな形">
            <Controller
              control={control}
              name="templateIds"
              render={({ field }) => {
                // 入力欄に改行区切りのひな形名を貼り付けると、
                // 該当ひな形をまとめて選択に追加する
                const handlePaste = (
                  event: React.ClipboardEvent<HTMLInputElement>,
                ) => {
                  const text = event.clipboardData.getData('text')
                  // 改行を含まない単一語は通常の検索絞り込みに
                  // 任せ、一括選択として乗っ取らない
                  if (!containsTemplateSeparator(text)) return

                  const tokens = parsePastedTemplateNames(text)
                  if (tokens.length === 0) return

                  // 生テキストが入力欄にそのまま入らないようにする
                  event.preventDefault()

                  const { matchedIds, unmatchedNames } = matchPastedTemplates(
                    tokens,
                    templatesQuery.data,
                  )

                  const currentIds = field.value ?? []
                  const nextIds = unionTemplateIds(currentIds, matchedIds)
                  const addedCount = nextIds.length - currentIds.length

                  if (addedCount > 0) field.onChange(nextIds)

                  // スナックバーは同時に 1 件しか出せないため、追加件数と
                  // 未マッチを 1 メッセージに統合して 1 回だけ表示する
                  const message = buildPasteResultMessage(
                    addedCount,
                    unmatchedNames,
                  )
                  if (message.severity === 'success') {
                    showSuccessSnackbar(message.text)
                  } else {
                    showInfoSnackbar(message.text)
                  }
                }

                // 一括出力(複数ひな形)を使えないユーザーには単一選択で提供する。
                // API 側の POST /reports/exports/bulk が管理者限定のため、
                // 複数選択させると送信時に必ず 403 になる
                if (!canBulkExport) {
                  return (
                    <Autocomplete
                      {...field}
                      options={templatesQuery.data}
                      getOptionKey={option => String(option.id)}
                      getOptionLabel={option => option.name}
                      onChange={(_, val) => field.onChange(val ? [val.id] : [])}
                      value={
                        templatesQuery.data.find(t =>
                          field.value?.includes(t.id),
                        ) ?? null
                      }
                      renderOption={(optProps, option) => {
                        const { key, ...rest } = optProps
                        return (
                          <li key={key} {...rest}>
                            <Typography variant="body2">
                              {option.name}
                            </Typography>
                          </li>
                        )
                      }}
                      renderInput={params => (
                        <TextField
                          {...params}
                          placeholder={
                            field.value?.length === 0 ? 'ひな形を選択' : ''
                          }
                          sx={{ width: '100%', backgroundColor: colors.white }}
                        />
                      )}
                    />
                  )
                }

                return (
                  <Autocomplete
                    {...field}
                    multiple
                    disableCloseOnSelect
                    options={templatesQuery.data}
                    getOptionKey={option => String(option.id)}
                    getOptionLabel={option => option.name}
                    onChange={(_, vals) => field.onChange(vals.map(v => v.id))}
                    value={
                      templatesQuery.data.filter(t =>
                        field.value?.includes(t.id),
                      ) ?? []
                    }
                    renderOption={(optProps, option, { selected }) => {
                      const { key, ...rest } = optProps
                      return (
                        <li key={key} {...rest}>
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                          >
                            <Checkbox size="small" checked={selected} />
                            <Typography variant="body2">
                              {option.name}
                            </Typography>
                          </Stack>
                        </li>
                      )
                    }}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index })
                        return (
                          <Chip
                            key={key}
                            label={option.name}
                            {...tagProps}
                            size="small"
                          />
                        )
                      })
                    }
                    renderInput={params => (
                      <TextField
                        {...params}
                        placeholder={
                          field.value?.length === 0 ? 'ひな形を選択' : ''
                        }
                        sx={{ width: '100%', backgroundColor: colors.white }}
                        // onPaste は実 input 要素に付ける。TextField 直付けだと
                        // ラッパーに流れて発火しないため slotProps.htmlInput 経由で配線する
                        slotProps={{
                          htmlInput: {
                            ...params.inputProps,
                            onPaste: handlePaste,
                          },
                        }}
                      />
                    )}
                  />
                )
              }}
            />
            <ErrorMessage>{errors['templateIds']?.message}</ErrorMessage>
            {isLobsterEnabled && (
              <Typography variant="caption">
                回答形式「AIラベル検査」の回答は現在出力できません
              </Typography>
            )}
          </TitleAndContent>

          {/* 現場 */}
          <TitleAndContent title="現場">
            <Controller
              name="placeNodeUuids"
              control={control}
              render={({ field }) => (
                <>
                  <Autocomplete
                    {...field}
                    multiple
                    disableCloseOnSelect
                    disabled={!userHasAssignedPlaces || isBulk}
                    options={effectivePlaces}
                    getOptionKey={option => option.uuid}
                    getOptionLabel={option => option.name}
                    onChange={(_, value) =>
                      field.onChange(value.map(v => v.uuid))
                    }
                    value={
                      field.value
                        ?.map(uuid =>
                          effectivePlaces.find(p => p.uuid === uuid),
                        )
                        .filter((p): p is NonNullable<typeof p> => p != null) ??
                      []
                    }
                    renderInput={params => (
                      <TextField
                        {...params}
                        placeholder={
                          isBulk
                            ? '一括出力時は全現場対象'
                            : userHasAssignedPlaces
                              ? '現場を選択'
                              : ''
                        }
                        sx={{ width: '100%', backgroundColor: colors.white }}
                      />
                    )}
                    renderOption={(props, option, { selected }) => (
                      <li {...props}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Checkbox size="small" checked={selected} />
                          {option.type === 'placeGroup' ? (
                            <PlaceGroupSVG
                              style={{ width: 16 }}
                              color={colors.action.active}
                            />
                          ) : (
                            <LocationOn
                              sx={{ width: 16, color: colors.action.active }}
                            />
                          )}
                          <span>{option.name}</span>
                        </Stack>
                      </li>
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index })
                        return (
                          <Chip
                            key={key}
                            label={option.name}
                            icon={<PlaceNodeIcon type={option.type} />}
                            {...tagProps}
                            sx={{ pl: 0.5 }}
                            size="small"
                          />
                        )
                      })
                    }
                  />
                  {isBulk ? (
                    <Typography variant="caption">
                      複数ひな形選択時は全現場を対象に出力されます
                    </Typography>
                  ) : userHasAssignedPlaces ? (
                    <ErrorMessage>
                      {errors['placeNodeUuids']?.message}
                    </ErrorMessage>
                  ) : (
                    <ErrorMessage>
                      ユーザーを編集し、所属する現場を設定してください
                    </ErrorMessage>
                  )}
                  {/* 単一ひな形時のみ、管理者は所属外現場も候補に含めるトグルが使える */}
                  {isOwner && !isBulk && (
                    <FormControlLabel
                      sx={{ ml: 0, mt: 0.5 }}
                      control={
                        <Switch
                          checked={includeNonBelongingPlaces}
                          onChange={e => {
                            setIncludeNonBelongingPlaces(e.target.checked)
                            // 候補が変わるので選択中現場をリセット
                            field.onChange([])
                          }}
                        />
                      }
                      label={
                        <Typography variant="caption">
                          所属していない現場も含める
                        </Typography>
                      }
                    />
                  )}
                </>
              )}
            ></Controller>
          </TitleAndContent>

          {/* 期間 */}
          <TitleAndContent title="期間">
            <LocalizationProvider
              dateAdapter={AdapterDateFns}
              adapterLocale={ja}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Controller
                  control={control}
                  name="startDate"
                  render={({ field }) => (
                    <DatePicker
                      orientation="portrait"
                      format="yyyy-MM-dd"
                      {...field}
                      onChange={(value: Date | null) => {
                        field.onChange(value ? toDateString(value) : null)
                        if (dirtyFields.endDate) {
                          void trigger('endDate')
                        }
                      }}
                      value={field.value ? parseISO(field.value) : null}
                      slotProps={{
                        textField: {
                          sx: {
                            flex: 1,
                            backgroundColor: colors.white,
                          },
                          slotProps: {
                            htmlInput: {
                              'aria-label': 'startDate',
                              placeholder: 'YYYY-MM-DD',
                            },
                          },
                        },
                      }}
                    />
                  )}
                />

                <Typography>〜</Typography>

                <Controller
                  control={control}
                  name="endDate"
                  render={({ field }) => (
                    <DatePicker
                      {...field}
                      orientation="portrait"
                      format="yyyy-MM-dd"
                      value={field.value ? parseISO(field.value) : null}
                      onChange={(value: Date | null) => {
                        field.onChange(value ? toDateString(value) : null)
                      }}
                      slotProps={{
                        textField: {
                          sx: {
                            flex: 1,
                            backgroundColor: colors.white,
                          },
                          slotProps: {
                            htmlInput: {
                              'aria-label': 'endDate',
                              placeholder: 'YYYY-MM-DD',
                            },
                          },
                        },
                      }}
                    />
                  )}
                />
              </Stack>
            </LocalizationProvider>
            <ErrorMessage>{errors['startDate']?.message}</ErrorMessage>
            <ErrorMessage>{errors['endDate']?.message}</ErrorMessage>
          </TitleAndContent>

          {/* 出力ファイル名 */}
          <TitleAndContent title="出力ファイル名">
            <Controller
              control={control}
              name="outputFileName"
              render={({ field }) => (
                <TextField
                  {...field}
                  sx={{ width: '100%', backgroundColor: colors.white }}
                  slotProps={{
                    htmlInput: { 'aria-label': '出力ファイル名' },
                  }}
                />
              )}
            />
            <ErrorMessage>{errors['outputFileName']?.message}</ErrorMessage>
          </TitleAndContent>
        </Stack>,
      )}
      <ReportExportDetailSendConfirmationV2
        isSendingData={isAddingReportExport || isAddingBulkReportExport}
        onCancel={sendConfirmationModal.close}
        onOk={handleSubmit(onSubmit)}
        open={sendConfirmationModal.isOpen}
        sendingDataAsString={{
          dateFromTo: `${stringifiedFormValues.startDateWithSlash} 〜 ${stringifiedFormValues.endDateWithSlash}`,
          outputFileName: formValues.outputFileName,
          places: stringifiedFormValues.placeNames,
          placeCount: resolvedPlaceNames.length,
          templateNames: stringifiedFormValues.templateNames,
          templateCount: resolvedTemplateNames.length,
        }}
      />
      <Dialog
        open={discardDialogOpen}
        onClose={() => setDiscardDialogOpen(false)}
        aria-labelledby="discard-dialog-title"
        maxWidth="sm"
      >
        <DialogTitle id="discard-dialog-title">
          変更した内容が破棄されますがよろしいですか
        </DialogTitle>
        <DialogActions>
          <Button
            color="primary"
            variant="outlined"
            onClick={() => setDiscardDialogOpen(false)}
          >
            キャンセル
          </Button>
          <Button
            onClick={() => {
              setDiscardDialogOpen(false)
              history.push('/reportExports')
            }}
            color="error"
            variant="contained"
          >
            はい
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={errorDialogOpen}
        onClose={() => setErrorDialogOpen(false)}
        aria-labelledby="error-dialog-title"
      >
        <DialogTitle id="error-dialog-title">エラーが発生しました</DialogTitle>
        <DialogActions>
          <Button onClick={() => setErrorDialogOpen(false)}>閉じる</Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
